import { createLdsPaymentLink, createDynamicPaymentLink } from "../services/fhpService";

const DEBUG_PREFIX = "[VP_AGENT_PAY_DEBUG]";

function logVpAgentPayDebug(tag, payload) {
  try {
    // eslint-disable-next-line no-console
    console.info(DEBUG_PREFIX, tag, payload);
  } catch {
    /* ignore */
  }
}

/**
 * @param {object|null|undefined} order
 * @returns {string|null}
 */
export function getFirstUnpaidLdsPaymentUrl(order) {
  if (!order || !Array.isArray(order.payments)) return null;
  for (const p of order.payments) {
    if (p.paid) continue;
    const u = p.paymentUrl || p.url;
    if (u != null && String(u).trim() !== "") return String(u).trim();
  }
  return null;
}

/**
 * @param {object|null|undefined} order
 * @returns {string|null}
 */
export function extractPrimaryPaymentUrlFromLdsOrder(order) {
  if (!order?.payments?.length) return null;
  const last = order.payments[order.payments.length - 1];
  const u = last?.paymentUrl || last?.url;
  if (u == null || String(u).trim() === "") return null;
  return String(u).trim();
}

export function isLdsPaymentExceedsRemainingError(data) {
  const text = `${data?.error || ""} ${data?.message || ""}`.toLowerCase();
  return text.includes("exceeds remaining");
}

export function remainingAmountFromLdsPaymentPayload(data) {
  const n = Number(
    data?.remainingAmount ??
      data?.remaining_amount ??
      data?.remaining_balance ??
      data?.remaining
  );
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function hebrewLdsPaymentExceedsRemainingMessage(remaining) {
  if (remaining != null && Number.isFinite(Number(remaining))) {
    return `סכום התשלום גבוה מהיתרה לתשלום (${Number(remaining).toLocaleString("he-IL")} ₪).`;
  }
  return "סכום התשלום גבוה מהיתרה לתשלום בהזמנה.";
}

export function capLdsPaymentCardToRemaining(currentCard, remaining) {
  const principal = Number(currentCard?.amount);
  const rem = Number(remaining);
  if (!Number.isFinite(principal) || !Number.isFinite(rem) || rem <= 0) {
    return currentCard;
  }
  if (principal <= rem + 0.02) return currentCard;
  const tash = Number(currentCard?.numberofpayments) || 1;
  const gross = Number(currentCard?.amountWithInterest);
  const ratio =
    tash > 1 && Number.isFinite(gross) && principal > 0 ? gross / principal : 1;
  const cappedGross =
    tash > 1 ? Math.round(rem * ratio * 100) / 100 : rem;
  return {
    ...currentCard,
    amount: rem,
    amountWithInterest: cappedGross,
    paymentInterest: Math.max(0, Math.round((cappedGross - rem) * 100) / 100),
  };
}

function normalizeOkOrder(data) {
  const order = { ...data.order };
  if (order.payments && Array.isArray(order.payments)) {
    order.payments = order.payments.map((payment) => {
      const paymentUrl = payment.paymentUrl || payment.url;
      return { ...payment, url: paymentUrl, paymentUrl };
    });
  }
  return order;
}

function failFromPayload(data, status) {
  if (data?.error_code === "PRICE_REFRESH") {
    return { ok: false, errorCode: "PRICE_REFRESH", data, status };
  }
  if (data?.error_code === "PRICE_MISMATCH") {
    return { ok: false, errorCode: "PRICE_MISMATCH", data, status };
  }
  const remaining = remainingAmountFromLdsPaymentPayload(data);
  const errorMessage = isLdsPaymentExceedsRemainingError(data)
    ? hebrewLdsPaymentExceedsRemainingMessage(remaining)
    : data?.message || data?.error || `שגיאת שרת (${status || 0})`;
  return { ok: false, errorMessage, data, status };
}

async function requestPaymentLink(currentCard, orderId, paymentApi, logContext) {
  return paymentApi === "dynamic"
    ? createDynamicPaymentLink(currentCard, orderId, {
        cardIndex: logContext.cardIndex,
        sourceField: logContext.sourceField || "flight_agent_payment",
      })
    : createLdsPaymentLink(currentCard, orderId);
}

async function parsePaymentLinkResponse(res, orderId) {
  let data;
  try {
    data = await res.json();
  } catch (parseError) {
    logVpAgentPayDebug("executeLdsCreditPaymentLink:parse_error", { orderId, parseError });
    return {
      ok: false,
      errorCode: "PARSE_ERROR",
      errorMessage: "שגיאה בפענוח תשובת השרת",
      status: res?.status,
    };
  }

  if (res.ok && res.status >= 200 && res.status < 300) {
    if (data.status === "ok" && data.order) {
      const order = normalizeOkOrder(data);
      return {
        ok: true,
        order,
        data,
        primaryPaymentUrl: extractPrimaryPaymentUrlFromLdsOrder(order),
        status: res.status,
      };
    }
    return failFromPayload(data, res.status);
  }

  return failFromPayload(data, res.status);
}

/**
 * Single implementation for LDS package credit: POST /getLDSPaymentUrl (used by CardPayment + vacation checkout).
 * Dynamic flights_new agent payments: POST /getDynamicPaymentUrl when `paymentApi` is `dynamic`.
 * `selectedResult` is accepted for call-site parity with CardPayment; the HTTP layer only uses (card, orderId).
 *
 * @returns {Promise<{
 *   ok: boolean;
 *   order?: object;
 *   data?: object;
 *   primaryPaymentUrl?: string | null;
 *   errorCode?: string;
 *   errorMessage?: string;
 *   status?: number;
 * }>}
 */
export async function executeLdsCreditPaymentLink({
  currentCard,
  orderId,
  selectedResult,
  logContext = {},
  paymentApi = "lds",
}) {
  void selectedResult;
  const endpoint =
    paymentApi === "dynamic" ? "/getDynamicPaymentUrl" : "/getLDSPaymentUrl";
  console.warn("[PENDING_OFFER_PAYMENT_URL_REQUEST]", {
    orderId,
    amount: currentCard?.amount ?? null,
    actualAmount: currentCard?.amount ?? null,
    tash: currentCard?.numberofpayments ?? null,
    currency: "ILS",
    paymentApi,
  });
  logVpAgentPayDebug("executeLdsCreditPaymentLink:call", {
    ...logContext,
    handlerName: logContext.handlerName || "executeLdsCreditPaymentLink",
    orderId,
    calledEndpoint: endpoint,
  });

  const first = await parsePaymentLinkResponse(
    await requestPaymentLink(currentCard, orderId, paymentApi, logContext),
    orderId
  );

  if (first.ok) {
    logVpAgentPayDebug("executeLdsCreditPaymentLink:ok", {
      ...logContext,
      orderId,
      payment_url: first.primaryPaymentUrl || null,
    });
    return first;
  }

  const remaining = remainingAmountFromLdsPaymentPayload(first.data);
  const shouldRetry =
    isLdsPaymentExceedsRemainingError(first.data) &&
    remaining != null &&
    Number(currentCard?.amount) - remaining > 0.02;

  if (!shouldRetry) {
    if (first.errorCode !== "PARSE_ERROR") {
      logVpAgentPayDebug("executeLdsCreditPaymentLink:http_error", {
        orderId,
        status: first.status,
        errorMessage: first.errorMessage,
      });
    }
    return first;
  }

  const cappedCard = capLdsPaymentCardToRemaining(currentCard, remaining);
  logVpAgentPayDebug("executeLdsCreditPaymentLink:retry_remaining", {
    orderId,
    remaining,
    previousAmount: currentCard?.amount ?? null,
    retryAmount: cappedCard?.amount ?? null,
  });
  const retry = await parsePaymentLinkResponse(
    await requestPaymentLink(cappedCard, orderId, paymentApi, logContext),
    orderId
  );
  if (retry.ok) {
    logVpAgentPayDebug("executeLdsCreditPaymentLink:ok", {
      ...logContext,
      orderId,
      payment_url: retry.primaryPaymentUrl || null,
      reusedRemaining: remaining,
    });
    return retry;
  }
  if (retry.errorCode !== "PARSE_ERROR") {
    logVpAgentPayDebug("executeLdsCreditPaymentLink:http_error", {
      orderId,
      status: retry.status,
      errorMessage: retry.errorMessage,
    });
  }
  return retry;
}
